import { useState, useEffect, Fragment } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ShieldCheck,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { AUDIT_ENTITY_TYPES } from '@/components/audit/entityTypes';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { useAuditLogs, useEntityAuditLogs } from '@/hooks/operations/useAuditLogs';
import { useUsers } from '@/hooks/settings/useUsers';
import { DiffView } from '@/components/audit/DiffView';
import { humanizeActorName } from '@/components/audit/actorName';
import {
  friendlyMessage,
  friendlyTitle,
  sanitizeAuditValue,
} from '@/components/audit/friendlyMessage';
import { useFieldMasks } from '@/hooks/usePermissions';
import type { AuditLog, AuditLogListParams } from '@/types/auditLog';

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_dashboard/dashboard/audit')({
  component: AuditLogsPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const PAGE_SIZE = 50;

const ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  restored: 'Restored',
  sent: 'Sent',
  approved: 'Approved',
  rejected: 'Rejected',
  expired: 'Expired',
  customer_approved: 'Customer Approved',
  status_changed: 'Status Changed',
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  archived: 'Archived',
  blocked: 'Blocked',
  unblocked: 'Unblocked',
  voided: 'Voided',
  written_off: 'Written Off',
  refunded: 'Refunded',
  submitted: 'Submitted',
  applied: 'Applied',
  closed: 'Closed',
  converted_to_estimate: 'Converted to Estimate',
  converted_to_job: 'Converted to Job',
  estimate_spawned: 'Estimate Spawned',
  estimate_materialized: 'Estimate Materialized',
  option_selected: 'Option Selected',
  invoice_generated: 'Invoice Generated',
  schedule_created: 'Schedule Created',
};

const ACTION_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  created: 'default',
  deleted: 'destructive',
  archived: 'destructive',
  blocked: 'destructive',
  voided: 'destructive',
  written_off: 'destructive',
  refunded: 'destructive',
  approved: 'default',
  customer_approved: 'default',
  rejected: 'secondary',
  expired: 'secondary',
  status_changed: 'secondary',
};

const ENTITY_TYPES = [
  'contact',
  'estimate',
  'proposal',
  'job',
  'invoice',
  'payment',
  'expense',
  'change_order',
  'progress_payment',
  'visit',
  'lead',
  AUDIT_ENTITY_TYPES.PRICEBOOK_ITEM,
  'membership_plan',
  'membership',
];

const FILTER_ACTIONS = [
  'created',
  'updated',
  'deleted',
  'sent',
  'approved',
  'rejected',
  'status_changed',
  'assigned',
  'refunded',
];

/**
 * A background write has no user behind it: the backend records
 * `actorType: 'SYSTEM'` with a null actorId/name (audit.service
 * `resolveActorFromContext`). Rendering `name ?? '—'` therefore showed an empty
 * actor for every scheduled refresh, sync and cascade, which reads as missing
 * data rather than "the system did this". The type is already on the wire; this
 * just says what it means.
 */
export function actorLabel(actor: AuditLog['actor']): string {
  // `humanizeActorName` also unpacks the `<kind>:<name>` machine-writer tokens
  // the audit trigger now falls back to when a write carried no session actor.
  const named = humanizeActorName(actor.name);
  if (named) return named;
  if (actor.type === 'SYSTEM') return 'System';
  return '—';
}

function ActionBadge({ action }: { action: string }) {
  return (
    <Badge variant={ACTION_VARIANTS[action] ?? 'outline'}>
      {ACTION_LABELS[action] ?? action.replace(/_/g, ' ')}
    </Badge>
  );
}

function EntityLabel({ type, label }: { type: string; label?: string | null }) {
  const formatted = type.replace(/_/g, ' ');
  return (
    <span>
      <span className="capitalize">{formatted}</span>
      {label ? <span className="text-muted-foreground"> · {label}</span> : null}
    </span>
  );
}

// ─── Skeleton rows ─────────────────────────────────────────────────────────────

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 10 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-4 w-36" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-28" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-40" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-24" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ─── On-demand diff (fetches entity history when expanded) ──────────────────

/**
 * When a row is expanded, fetch the entity's audit history (server-side
 * reconstruction via findByEntityWithDiffs) and use the matching row's
 * previousState. Scoped per entity so Aurora I/O stays bounded — only
 * entities the user actually inspects are loaded.
 *
 * React Query dedupes by query key, so expanding multiple rows for the
 * same entity triggers only one request.
 */
function OnDemandDiff({ log, fieldMasks }: { log: AuditLog; fieldMasks: readonly string[] }) {
  const { data, isLoading, isError } = useEntityAuditLogs(log.entityType, log.entityId);

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Loading history…</p>;
  }

  // Fallback: show forward-only diff if history fetch fails
  if (isError || !data) {
    return (
      <DiffView
        changes={log.changes as Record<string, unknown>}
        isDelete={log.action === 'deleted'}
        fieldMasks={fieldMasks}
      />
    );
  }

  // Find the matching entry — it already has previousState reconstructed server-side
  const match = data.logs.find((l) => l.id === log.id);
  const previousState =
    (match?.previousState as Record<string, unknown> | null | undefined) ?? null;

  return (
    <DiffView
      changes={log.changes as Record<string, unknown>}
      previousState={previousState}
      isDelete={log.action === 'deleted'}
      fieldMasks={fieldMasks}
    />
  );
}

// ─── Expandable row ───────────────────────────────────────────────────────────

function AuditRow({ log }: { log: AuditLog }) {
  const [expanded, setExpanded] = useState(false);
  const companyTimezone = useCompanyTimezone();
  const fieldMasks = useFieldMasks();
  const maskedChanges = sanitizeAuditValue(log.changes, fieldMasks);
  // Render the audit time in the company tz, then append millisecond precision
  // (tz-independent) for forensic ordering, which Intl can't emit directly.
  const milliseconds = String(new Date(log.createdAt).getMilliseconds()).padStart(3, '0');
  const hasDetails =
    (maskedChanges != null &&
      typeof maskedChanges === 'object' &&
      Object.keys(maskedChanges as object).length > 0) ||
    log.statusFrom ||
    log.statusTo;

  return (
    <Fragment>
      <TableRow
        className={hasDetails ? 'cursor-pointer hover:bg-muted/50' : undefined}
        onClick={hasDetails ? () => setExpanded(!expanded) : undefined}
      >
        <TableCell className="whitespace-nowrap text-sm align-top pt-3">
          <div className="flex items-start gap-1">
            {hasDetails && (
              <ChevronDown
                className={`size-3.5 text-muted-foreground transition-transform mt-0.5 ${expanded ? 'rotate-180' : ''}`}
              />
            )}
            <div className="flex flex-col">
              <span>
                {formatDateTimeInTimeZone(log.createdAt, companyTimezone, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <span className="text-muted-foreground text-xs">
                {formatDateTimeInTimeZone(log.createdAt, companyTimezone, {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hourCycle: 'h23',
                })}
                .{milliseconds}
              </span>
            </div>
          </div>
        </TableCell>
        <TableCell className="align-top pt-3">
          <ActionBadge action={log.action} />
        </TableCell>
        <TableCell className="align-top pt-3">
          <EntityLabel type={log.entityType} label={log.entityLabel} />
        </TableCell>
        {/* `line-clamp-2` below only clamps if the cell is allowed to wrap;
            the atom's nowrap default turned it into a one-line cut. */}
        <TableCell className="text-sm align-top pt-3 whitespace-normal wrap-anywhere">
          <div className="max-w-[400px] min-w-0">
            <div className="font-medium text-foreground truncate">{friendlyTitle(log)}</div>
            <div className="text-muted-foreground text-xs mt-0.5 line-clamp-2">
              {friendlyMessage(log, { fieldMasks })}
            </div>
          </div>
        </TableCell>
        <TableCell className="align-top pt-3 whitespace-nowrap">
          <div className="text-sm font-medium">{actorLabel(log.actor)}</div>
          {log.actor.email && (
            <div className="text-muted-foreground text-xs">{log.actor.email}</div>
          )}
        </TableCell>
      </TableRow>

      {expanded && hasDetails && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={5} className="py-3 px-6">
            <div className="space-y-2">
              {/* Status change */}
              {(log.statusFrom || log.statusTo) && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground font-medium">Status:</span>
                  {log.statusFrom && (
                    <Badge variant="outline" className="text-[10px]">
                      {log.statusFrom}
                    </Badge>
                  )}
                  {log.statusFrom && log.statusTo && (
                    <span className="text-muted-foreground">→</span>
                  )}
                  {log.statusTo && (
                    <Badge variant="default" className="text-[10px]">
                      {log.statusTo}
                    </Badge>
                  )}
                </div>
              )}

              {/* Field changes — fetch entity history on-demand to build before/after */}
              {maskedChanges != null &&
                typeof maskedChanges === 'object' &&
                Object.keys(maskedChanges as object).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Changes:</p>
                    <OnDemandDiff log={log} fieldMasks={fieldMasks} />
                  </div>
                )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function AuditLogsPage() {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState<string>('all');
  const [entityType, setEntityType] = useState<string>('all');
  const [userId, setUserId] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 350);
  const { data: usersData } = useUsers();
  const users = (usersData?.data ?? []) as Array<{
    id: string;
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string;
  }>;

  const params: AuditLogListParams = {
    page,
    limit: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(action !== 'all' && { action }),
    ...(entityType !== 'all' && { entityType }),
    ...(userId !== 'all' && { actorId: userId }),
    ...(startDate && { startDate: new Date(startDate).toISOString() }),
    ...(endDate && { endDate: new Date(endDate + 'T23:59:59').toISOString() }),
  };

  const { data, isLoading, isError, refetch } = useAuditLogs(params);

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, action, entityType, userId, startDate, endDate]);

  const clearFilters = () => {
    setSearch('');
    setAction('all');
    setEntityType('all');
    setUserId('all');
    setStartDate('');
    setEndDate('');
  };

  const hasFilters =
    search || action !== 'all' || entityType !== 'all' || userId !== 'all' || startDate || endDate;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="text-primary size-6" />
          <div>
            <h1 className="text-2xl font-semibold">Audit Logs</h1>
            <p className="text-muted-foreground text-sm">
              Track all changes and activity across your account
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isLoading}>
          <RefreshCw className={`mr-2 size-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-52">
          <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
          <Input
            placeholder="Search messages, entities…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Select value={action} onValueChange={(v) => setAction(v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {FILTER_ACTIONS.map((a) => (
              <SelectItem key={a} value={a}>
                {ACTION_LABELS[a] ?? a}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={userId} onValueChange={(v) => setUserId(v)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="User" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.displayName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground">From</label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-36"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground">To</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-36"
          />
        </div>

        <Select value={entityType} onValueChange={(v) => setEntityType(v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Entity type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {ENTITY_TYPES.map((et) => (
              <SelectItem key={et} value={et} className="capitalize">
                {et.replace(/_/g, ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      {isError ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertCircle className="text-destructive size-8" />
          <p className="text-muted-foreground text-sm">Failed to load audit logs</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Activity</TableHead>
                <TableHead>Actor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <SkeletonRows />
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-16 text-center">
                    <p className="text-muted-foreground text-sm">No audit log entries found</p>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => <AuditRow key={log.id} log={log} />)
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {!isError && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {total.toLocaleString()} log entr{total !== 1 ? 'ies' : 'y'}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-sm">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
